import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/site/SiteNav";
import { Footer } from "@/components/site/Sections";
import {
  Target,
  CheckCircle2,
  ArrowRight,
  ChevronRight,
  Sparkles,
  BarChart3,
  TrendingUp,
  Users,
  Award,
  Globe,
  Clock,
  ShieldCheck,
  Check,
  Building2,
  FileCheck,
  Layers,
  Search,
  Zap,
  MousePointerClick,
  DollarSign,
  Share2,
  Briefcase,
  PieChart,
  ShoppingBag,
  Tv,
} from "lucide-react";

export const Route = createFileRoute("/services_/performance-marketing")({
  head: () => ({
    meta: [
      {
        title:
          "Performance Marketing Services — KMWAdvertising · Google, Meta & LinkedIn Ads",
      },
      {
        name: "description",
        content:
          "Performance Marketing Services That Drive Measurable Growth, Qualified Leads and Higher Return on Ad Spend. Google Ads, Meta Ads, LinkedIn Ads & PMax by KMWA LLP.",
      },
      {
        property: "og:title",
        content: "Performance Marketing Services — KMWAdvertising",
      },
      {
        property: "og:description",
        content:
          "Turn Every Advertising Investment Into Measurable Business Growth. Scalable lead generation, e-commerce ROAS, and customer acquisition.",
      },
    ],
  }),
  component: PerformanceMarketingServicePage,
});

export function PerformanceMarketingServicePage() {
  const whyPerformanceMatters = [
    {
      title: "Measurable Results",
      desc: "Every click, impression, lead, purchase and conversion can be tracked, measured and analysed, providing complete visibility into campaign performance.",
      icon: BarChart3,
    },
    {
      title: "Budget Efficiency",
      desc: "Advertising budgets are allocated based on real performance data rather than assumptions, helping businesses maximize every marketing investment.",
      icon: DollarSign,
    },
    {
      title: "Continuous Optimisation",
      desc: "Campaigns are monitored daily to improve audience targeting, bidding strategies, creative performance and conversion rates.",
      icon: Zap,
    },
    {
      title: "Faster Customer Acquisition",
      desc: "Performance marketing enables businesses to reach high-intent audiences actively searching for products and services, reducing time to customer conversion.",
      icon: MousePointerClick,
    },
    {
      title: "Scalable Growth",
      desc: "As campaigns become more profitable, budgets can be increased strategically while maintaining efficiency and return on investment.",
      icon: TrendingUp,
    },
  ];

  const googleSearchTactics = [
    "Comprehensive keyword research",
    "Campaign architecture & match types",
    "Responsive Search Ads (RSA)",
    "Dynamic Search Ads (DSA)",
    "Ad extensions & callouts",
    "Smart bid management",
    "Audience segmentation",
    "Quality Score optimisation",
    "Conversion tracking setup",
    "Negative keyword optimization",
  ];

  const googleDisplayTactics = [
    "High-impact display banners",
    "Responsive display ads",
    "Dynamic remarketing campaigns",
    "Custom intent audience targeting",
    "Contextual keyword targeting",
    "Placement & publisher optimization",
  ];

  const youtubeFormats = [
    "Skippable in-stream video ads",
    "Non-skippable 15s commercial ads",
    "Bumper ads (6-second unskippable)",
    "In-feed video discovery campaigns",
    "YouTube Shorts vertical video ads",
  ];

  const metaFacebookObjectives = [
    "Brand awareness & reach",
    "High-intent website traffic",
    "Lead generation & instant forms",
    "E-commerce sales & conversions",
    "Mobile app installs & events",
    "Local store traffic & event promotion",
  ];

  const metaInstagramPlacements = [
    "Instagram Feed Ads",
    "Instagram Stories Video Ads",
    "Instagram Reels Vertical Ads",
    "Explore Tab Placements",
    "Carousel Product Showcase Ads",
    "Collection Storefront Ads",
  ];

  const linkedinFormats = [
    "Sponsored Content (Single image, video, carousel)",
    "Lead Generation Forms (In-app instant submit)",
    "Conversation Ads & Message Ads",
    "Text Ads & Spotlight Ads",
    "Dynamic Personalized Ads",
    "Video Campaigns & Event Promotion",
    "Recruitment & Employer Branding",
    "Account-Based Marketing (ABM) Playbooks",
  ];

  const linkedinSectors = [
    "B2B Enterprise Services",
    "SaaS & Tech Platforms",
    "Professional Consulting",
    "Manufacturing & Supply Chain",
    "Healthcare & Life Sciences",
    "Higher Education & Executive Ed",
    "Financial Services & Fintech",
    "Enterprise Technology Solutions",
  ];

  const audienceResearchPoints = [
    "Demographic & socio-economic profiles",
    "Interests & affinity clustering",
    "In-market purchase intent signals",
    "Online digital browsing behavior",
    "Search activity & keyword intent",
    "Customer persona journey mapping",
    "Geographic & device usage patterns",
    "Customer Lifetime Value (LTV) cohorts",
    "First-party CRM audience integration",
  ];

  const strategySteps = [
    {
      num: "01",
      title: "Business Goal Alignment",
      desc: "Understanding your commercial objectives and defining measurable success metrics (CAC, ROAS, CPL).",
      icon: Target,
    },
    {
      num: "02",
      title: "Competitive Analysis",
      desc: "Evaluating competitors' advertising strategies, creative positioning, and market share opportunities.",
      icon: Search,
    },
    {
      num: "03",
      title: "Keyword Research",
      desc: "Identifying high-intent search terms that align with customer needs and business revenue goals.",
      icon: Layers,
    },
    {
      num: "04",
      title: "Channel Selection",
      desc: "Choosing the right mix of Google Ads, Meta Ads, LinkedIn Ads, YouTube, Microsoft Advertising, and Amazon Ads.",
      icon: Share2,
    },
    {
      num: "05",
      title: "Budget Allocation",
      desc: "Distributing advertising investment across campaigns and channels based on expected return and priorities.",
      icon: DollarSign,
    },
  ];

  return (
    <div className="dark relative min-h-screen bg-background text-foreground flex flex-col w-full overflow-x-clip">
      <SiteNav />

      <main className="flex-1 w-full overflow-x-clip pt-24 md:pt-28">
        {/* HERO SECTION */}
        <section className="relative overflow-hidden py-14 sm:py-20 md:py-24 border-b border-white/10">
          <div className="absolute inset-0 grid-bg opacity-15" aria-hidden />
          <div className="glow-orb -left-32 top-10 h-[450px] w-[450px] bg-[var(--glow-purple)] opacity-25" />
          <div className="glow-orb right-[-100px] top-20 h-[400px] w-[400px] bg-[var(--glow-cyan)] opacity-25" />

          <div className="container-x relative z-10">
            {/* Breadcrumb */}
            <div className="flex flex-wrap items-center gap-2.5 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
              <Link to="/" className="hover:text-white transition">
                Home
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <Link to="/services" className="hover:text-white transition">
                Services
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <span className="text-[var(--glow-purple)] font-semibold">Performance Marketing</span>
            </div>

            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-muted-foreground border border-white/10 mb-6">
                <Target className="h-3.5 w-3.5 text-[var(--glow-purple)]" />
                Digital Media Capabilities
              </div>

              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.12] tracking-tight text-white">
                Performance Marketing Services That Drive Measurable Growth, Qualified Leads and Higher Return on Ad Spend
              </h1>

              <p className="mt-4 text-base font-mono text-white/80 uppercase tracking-widest">
                Performance Marketing (Google Ads, Meta Ads, LinkedIn Ads, Amazon Ads etc)
              </p>

              <p className="mt-4 text-lg sm:text-xl font-accent italic text-[var(--glow-purple)] font-medium border-l-2 border-[var(--glow-purple)] pl-4 py-1">
                Turn Every Advertising Investment Into Measurable Business Growth
              </p>

              <div className="mt-6 space-y-4 text-base sm:text-lg text-white/90 font-normal leading-relaxed">
                <p>
                  Modern marketing is no longer measured by impressions alone. Businesses today expect advertising to generate qualified leads, online sales, customer acquisitions and measurable return on investment. This shift has made performance marketing one of the most effective approaches for brands seeking predictable, scalable and data-driven growth.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  Performance marketing focuses on outcomes rather than assumptions. Every campaign is designed around clearly defined objectives such as increasing website traffic, generating enquiries, driving e-commerce sales, improving app installs or maximising return on advertising spend (ROAS). Through continuous optimisation, audience analysis and creative testing, businesses gain greater control over marketing budgets while improving campaign efficiency.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  At <strong className="text-white">KMWAdvertising LLP</strong>, we combine strategic planning, advanced audience targeting, creative excellence and real-time analytics to build performance marketing campaigns that deliver meaningful business results across Google Ads, Meta Ads, LinkedIn Ads, YouTube Ads, Amazon Advertising and programmatic media.
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                <a href="/contact-us" className="btn-hero !py-3.5 !px-7">
                  Request Performance Audit & Strategy
                  <ArrowRight className="h-4 w-4" />
                </a>
                <Link to="/services" className="btn-ghost !py-3.5 !px-7">
                  Explore All Services
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* WHY PERFORMANCE MARKETING MATTERS */}
        <section className="py-14 sm:py-20 border-b border-white/10 bg-white/[0.01]">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                Predictable Revenue Growth
              </span>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-white">
                Why Performance Marketing Matters
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mt-3 leading-relaxed">
                Traditional advertising often measures success by reach alone. Performance marketing bridges the gap between advertising investment and business revenue by using real-time data to optimize every stage of the customer funnel.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {whyPerformanceMatters.map((item, idx) => {
                const PIcon = item.icon;
                return (
                  <div
                    key={idx}
                    className="p-6 sm:p-8 rounded-2xl glass-strong border border-white/10 hover:border-white/20 transition duration-300 flex flex-col justify-between group"
                  >
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)]">
                          PILLAR 0{idx + 1}
                        </span>
                        <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white">
                          <PIcon className="h-5 w-5 text-[var(--glow-purple)]" />
                        </div>
                      </div>
                      <h3 className="font-display text-xl font-bold text-white mb-2">
                        {item.title}
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* PLATFORM DEEP-DIVES: GOOGLE, META & LINKEDIN */}
        <section className="py-16 md:py-24 border-b border-white/10 relative">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                Multi-Platform Mastery
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Paid Media Execution Across Platforms
              </h2>
            </div>

            {/* GOOGLE ADS */}
            <div className="rounded-3xl glass-strong border border-white/15 p-6 sm:p-10 md:p-12 mb-12 relative overflow-hidden shadow-2xl">
              <div className="glow-orb -top-32 -right-32 h-96 w-96 rounded-full bg-[var(--glow-purple)] opacity-15" />

              <div className="grid lg:grid-cols-12 gap-8 items-start relative z-10">
                <div className="lg:col-span-5 space-y-4">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono text-[var(--glow-purple)] bg-[var(--glow-purple)]/10 border border-[var(--glow-purple)]/30">
                    <Search className="h-3.5 w-3.5" />
                    High-Intent Search & PMax
                  </div>
                  <h3 className="font-display text-2xl sm:text-3xl font-bold text-white">
                    Google Ads Management
                  </h3>
                  <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider">
                    Capture High-Intent Customers at the Moment They Search
                  </p>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    Google processes billions of searches every day. Our Google Ads specialists develop data-driven campaigns across Search, Display Network, YouTube Ads, Google Shopping, and AI-powered Performance Max.
                  </p>
                </div>

                <div className="lg:col-span-7 bg-[#06060c]/90 rounded-2xl p-6 border border-white/10 space-y-6">
                  <div>
                    <h4 className="text-xs font-mono uppercase tracking-widest text-white/80 mb-3 block">
                      Search Advertising Tactics:
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {googleSearchTactics.map((st, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                          <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                          <span>{st}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="pt-4 border-t border-white/10 grid sm:grid-cols-2 gap-4">
                    <div>
                      <h5 className="text-xs font-mono uppercase tracking-widest text-white/80 mb-2">Display & Remarketing</h5>
                      <div className="space-y-1">
                        {googleDisplayTactics.map((dt, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Check className="h-3 w-3 text-[var(--glow-purple)] shrink-0" />
                            <span>{dt}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h5 className="text-xs font-mono uppercase tracking-widest text-white/80 mb-2">YouTube Formats</h5>
                      <div className="space-y-1">
                        {youtubeFormats.map((yf, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Check className="h-3 w-3 text-[var(--glow-purple)] shrink-0" />
                            <span>{yf}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* META ADS (FACEBOOK & INSTAGRAM) */}
            <div className="grid lg:grid-cols-2 gap-8 mb-12">
              {/* FACEBOOK & INSTAGRAM */}
              <div className="p-8 sm:p-10 rounded-3xl glass-strong border border-white/15 space-y-6">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono text-pink-400 bg-pink-500/10 border border-pink-500/30 mb-3">
                    <Share2 className="h-3.5 w-3.5" />
                    Paid Social Funnels
                  </div>
                  <h3 className="font-display text-2xl font-bold text-white">Meta Ads Management</h3>
                  <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mt-1">
                    Reach Customers Across Facebook, Instagram, Messenger & WhatsApp
                  </p>
                </div>

                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  Meta advertising enables businesses to build awareness, generate leads, increase traffic and drive online sales through sophisticated audience targeting and dynamic catalogue ads.
                </p>

                <div className="grid sm:grid-cols-2 gap-4 pt-2">
                  <div>
                    <h5 className="text-xs font-mono uppercase tracking-widest text-white/80 mb-2">Facebook Objectives:</h5>
                    <div className="space-y-1">
                      {metaFacebookObjectives.map((mfo, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                          <Check className="h-3 w-3 text-pink-400 shrink-0" />
                          <span>{mfo}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h5 className="text-xs font-mono uppercase tracking-widest text-white/80 mb-2">Instagram Placements:</h5>
                    <div className="space-y-1">
                      {metaInstagramPlacements.map((mip, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                          <Check className="h-3 w-3 text-pink-400 shrink-0" />
                          <span>{mip}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* LINKEDIN ADS */}
              <div className="p-8 sm:p-10 rounded-3xl glass-strong border border-white/15 space-y-6 bg-[#06060c]/90">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono text-sky-400 bg-sky-500/10 border border-sky-500/30 mb-3">
                    <Briefcase className="h-3.5 w-3.5" />
                    B2B & Enterprise Targeting
                  </div>
                  <h3 className="font-display text-2xl font-bold text-white">LinkedIn Advertising</h3>
                  <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mt-1">
                    Connect with Decision-Makers & Business Professionals
                  </p>
                </div>

                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  LinkedIn is the leading platform for B2B marketing, enabling reach based on job title, industry, company size, seniority, and Account-Based Marketing (ABM) lists.
                </p>

                <div className="grid sm:grid-cols-2 gap-4 pt-2">
                  <div>
                    <h5 className="text-xs font-mono uppercase tracking-widest text-white/80 mb-2">LinkedIn Formats:</h5>
                    <div className="space-y-1">
                      {linkedinFormats.slice(0, 4).map((lf, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                          <Check className="h-3 w-3 text-sky-400 shrink-0" />
                          <span>{lf}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h5 className="text-xs font-mono uppercase tracking-widest text-white/80 mb-2">Key B2B Sectors:</h5>
                    <div className="space-y-1">
                      {linkedinSectors.slice(0, 4).map((ls, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                          <Check className="h-3 w-3 text-sky-400 shrink-0" />
                          <span>{ls}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* AUDIENCE RESEARCH & STRATEGY PROCESS */}
            <div className="grid lg:grid-cols-12 gap-8 items-start">
              {/* Audience Research */}
              <div className="lg:col-span-5 p-8 rounded-3xl glass-strong border border-white/10 space-y-4">
                <h4 className="font-display text-xl font-bold text-white flex items-center gap-2">
                  <Users className="h-5 w-5 text-[var(--glow-purple)]" />
                  Audience Research & Targeting
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  Reach the right people at the right moment through data signals, first-party audience profiling, and behavioral clustering:
                </p>
                <div className="space-y-2 pt-2">
                  {audienceResearchPoints.map((arp, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                      <span>{arp}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Strategy & Planning Steps */}
              <div className="lg:col-span-7 space-y-4">
                <div className="border-b border-white/10 pb-3">
                  <h4 className="font-display text-xl font-bold text-white">
                    Campaign Strategy & Planning Process
                  </h4>
                  <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mt-1">
                    From Goal Alignment to Scalable Execution
                  </p>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  {strategySteps.map((step, idx) => {
                    const SIcon = step.icon;
                    return (
                      <div key={idx} className="p-4 rounded-xl bg-white/[0.03] border border-white/5 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-xs text-[var(--glow-purple)] font-bold">{step.num}</span>
                          <SIcon className="h-4 w-4 text-white/70" />
                        </div>
                        <h5 className="text-sm font-bold text-white">{step.title}</h5>
                        <p className="text-xs text-muted-foreground leading-snug">{step.desc}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CLOSING CTA BANNER */}
        <section className="py-20 md:py-28 relative overflow-hidden">
          <div className="glow-orb left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] bg-[var(--glow-purple)] opacity-20" />

          <div className="container-x relative z-10 text-center max-w-4xl">
            <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-widest text-muted-foreground border border-white/10 mb-6">
              <Target className="h-3.5 w-3.5 text-[var(--glow-purple)]" />
              Performance Growth Advisory
            </div>

            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight">
              Ready to Scale Your Business with High-ROAS Performance Marketing?
            </h2>

            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Connect with KMWAdvertising LLP's performance marketing specialists to audit your ad accounts and build a scalable acquisition campaign.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="/contact-us" className="btn-hero w-full sm:w-auto">
                Request Performance Proposal
                <ArrowRight className="h-4 w-4" />
              </a>
              <Link to="/services" className="btn-ghost w-full sm:w-auto">
                Back to All Services
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
